/**
 * SearchableSelect — a single-value picker with a type-ahead search box (ADO 406).
 *
 * Replaces bare `<select>` assignee pickers, which have no way to filter as the
 * org grows. Values are ids (not display strings), so callers can pass the
 * selected id straight to a mutation.
 *
 * The listbox renders in a portal with fixed positioning so it is never clipped
 * by a scrolling/overflow-hidden ancestor (these pickers live inside side panels
 * and modals). It repositions on scroll/resize, flips above the trigger when there
 * is no usable room below it (see `computeListboxPlacement`), and closes on
 * outside click.
 *
 * Filtering:
 * - default: filtered locally against `label` + `meta` + `searchText`
 * - pass `onSearchChange` to drive filtering server-side; the component then
 *   renders `options` as-is and leaves debouncing/fetching to the caller.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ChevronDown, Search } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { Badge } from '../Badge';
import {
  computeListboxPlacement,
  getClippingBoundaryBottom,
  type ListboxPlacement,
} from './listboxPlacement';

const LISTBOX_Z_INDEX = theme.zIndex.modal + 30;

/** Next non-disabled index in `direction` from `from`, or `from` unchanged if none exists. */
const nextEnabledIndex = (
  options: SearchableSelectOption[],
  from: number,
  direction: 1 | -1,
): number => {
  for (let n = from + direction; n >= 0 && n < options.length; n += direction) {
    if (!options[n].disabled) return n;
  }
  return from;
};

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary line, e.g. "Phoenix North Clinic · BCBA". */
  meta?: string;
  /** Extra text included in local search matching but not displayed. */
  searchText?: string;
  /** Small pill rendered after the label, e.g. "Overseeing BCBA". */
  badge?: string;
  /** Rendered greyed-out and not selectable (keyboard or mouse); stays visible/filterable. */
  disabled?: boolean;
  /** Shown in place of `meta` when `disabled` is true, e.g. "Cannot own this workflow". */
  disabledHint?: string;
}

export interface SearchableSelectProps {
  value: string | null | undefined;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  'aria-label'?: string;
  className?: string;
  /** Provide to filter server-side; receives the raw query on every keystroke. */
  onSearchChange?: (query: string) => void;
}

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search by name, location, or role...',
  emptyText = 'No matches.',
  disabled = false,
  isLoading = false,
  'aria-label': ariaLabel,
  className,
  onSearchChange,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<ListboxPlacement | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const serverFiltered = typeof onSearchChange === 'function';

  const filtered = useMemo(() => {
    if (serverFiltered) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.label, o.meta ?? '', o.searchText ?? ''].some((f) => f.toLowerCase().includes(q)),
    );
  }, [options, query, serverFiltered]);

  const selected = options.find((o) => o.value === value) ?? null;

  const position = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPlacement(
      computeListboxPlacement(r, getClippingBoundaryBottom(el), window.innerHeight),
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    position();
    // Keep the listbox anchored while the underlying panel scrolls.
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    return () => {
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    };
  }, [open, position]);

  // Depends on `placement` as well as `open`: the listbox portal only renders once
  // a position has been measured, so on the open commit the input ref is still null.
  useEffect(() => {
    if (open && placement) inputRef.current?.focus();
  }, [open, placement]);

  // Keep the active (highlighted) row off a disabled option — on open, and whenever
  // the filtered list changes shape (typing), `activeIndex` can land on one.
  useEffect(() => {
    if (filtered.length === 0) return;
    if (filtered[activeIndex]?.disabled) {
      const firstEnabled = filtered.findIndex((o) => !o.disabled);
      if (firstEnabled !== -1) setActiveIndex(firstEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (listboxRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery('');
    if (serverFiltered) onSearchChange?.('');
    triggerRef.current?.focus();
  };

  const pick = (option: SearchableSelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    setQuery('');
    if (serverFiltered) onSearchChange?.('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Stop the keydown from bubbling to a document-level Escape handler (e.g. Modal's
      // closeOnEscape) — Escape here should close only the listbox, not a dialog it's inside.
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => nextEnabledIndex(filtered, i, 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => nextEnabledIndex(filtered, i, -1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = filtered[activeIndex];
      if (option) pick(option);
    }
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setActiveIndex(0);
    if (serverFiltered) onSearchChange?.(next);
  };

  return (
    <>
      <Trigger
        type="button"
        ref={triggerRef}
        className={className}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        onClick={() => {
          if (disabled) return;
          setActiveIndex(0);
          setOpen((o) => !o);
        }}
      >
        <TriggerLabel $placeholder={!selected}>{selected?.label ?? placeholder}</TriggerLabel>
        <ChevronDown aria-hidden="true" />
      </Trigger>

      {open &&
        placement &&
        createPortal(
          <Listbox
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            style={{
              top: placement.top,
              bottom: placement.bottom,
              left: placement.left,
              minWidth: placement.width,
              maxHeight: placement.maxHeight,
            }}
            onKeyDown={handleKeyDown}
          >
            <SearchRow>
              <Search aria-hidden="true" />
              <SearchInput
                ref={inputRef}
                type="text"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(e) => handleQueryChange(e.target.value)}
              />
            </SearchRow>

            {isLoading ? (
              <StatusRow>Loading...</StatusRow>
            ) : filtered.length === 0 ? (
              <StatusRow>{emptyText}</StatusRow>
            ) : (
              <OptionList>
                {filtered.map((option, index) => {
                  const displayMeta = option.disabled ? option.disabledHint : option.meta;
                  return (
                    <OptionRow
                      key={option.value}
                      role="option"
                      aria-selected={option.value === value}
                      aria-disabled={option.disabled || undefined}
                      $active={index === activeIndex && !option.disabled}
                      $disabled={!!option.disabled}
                      onMouseEnter={() => {
                        if (!option.disabled) setActiveIndex(index);
                      }}
                      onClick={() => pick(option)}
                    >
                      <span className="labelRow">
                        <span className="label">{option.label}</span>
                        {option.badge && <Badge variant="info">{option.badge}</Badge>}
                      </span>
                      {displayMeta && <span className="meta">{displayMeta}</span>}
                    </OptionRow>
                  );
                })}
              </OptionList>
            )}
          </Listbox>,
          document.body,
        )}
    </>
  );
}

// ============================================================================
// Styles
// ============================================================================

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  min-width: 0;
  min-height: 44px; /* WCAG compliant touch target — matches Select */
  padding: 0 ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  text-align: left;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const TriggerLabel = styled.span<{ $placeholder: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $placeholder, theme }) =>
    $placeholder ? theme.colors.text.secondary : theme.colors.text.primary};
`;

const Listbox = styled.div`
  position: fixed;
  z-index: ${LISTBOX_Z_INDEX};
  width: max-content;
  max-width: min(360px, calc(100vw - ${({ theme }) => theme.spacing[8]}));
  max-height: min(320px, calc(100vh - ${({ theme }) => theme.spacing[8]}));
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OptionList = styled.div`
  overflow-y: auto;
`;

const OptionRow = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ $active, theme }) => ($active ? theme.colors.backgrounds.page : 'transparent')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  .labelRow {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    min-width: 0;
  }

  .label {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }
`;

const StatusRow = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;
